/**
 * @voltxampere/engine — Resistor DC stamp
 *
 * A resistor of resistance `R` between nets `p1` and `p2` contributes
 * a conductance `G = 1/R` to the four MNA cells:
 *
 *   Y[p1][p1] += G,   Y[p2][p2] += G,
 *   Y[p1][p2] -= G,   Y[p2][p1] -= G.
 *
 * Either pin may be ground; `addY` silently drops the corresponding
 * cells (see `stamps/context.ts`). The stamp therefore needs no
 * "is on ground?" branch.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { StampParameterError } from "@voltxampere/shared";
import { defineDCStamp } from "../definition.js";
import { getTerminalNetId, getNumericParam } from "../terminal-utils.js";
export const resistorDCStamp = (ctx, component) => {
    const resistance = getNumericParam(component, "resistance");
    if (!(resistance > 0) || !Number.isFinite(resistance)) {
        // Sprint 55 typed-error migration. Recovery Center hooks into
        // E_VALIDATION_STAMP_PARAM to surface an Inspector "Open & fix"
        // CTA rather than a raw technical message.
        throw new StampParameterError(`resistorDCStamp: component "${component.id}" has non-positive resistance ${String(resistance)}`, {
            meta: {
                stampType: "resistor",
                parameter: "resistance",
                received: resistance,
                expected: "> 0 and finite",
                componentId: component.id,
            },
        });
    }
    const g = 1 / resistance;
    const a = ctx.nodeIndex(getTerminalNetId(component, "p1"));
    const b = ctx.nodeIndex(getTerminalNetId(component, "p2"));
    ctx.addY(a, a, g);
    ctx.addY(b, b, g);
    ctx.addY(a, b, -g);
    ctx.addY(b, a, -g);
};
/** Pure conductance contribution — no augmented MNA rows. */
export const resistorDCStampDefinition = defineDCStamp("resistor", resistorDCStamp, []);
//# sourceMappingURL=resistor.js.map